import React, { forwardRef } from 'react';
import { FormHelperText, FormHelperTextProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { extractPounceProps } from '../../utils/extractPounceProps';
import { mapPouncePropsToSx } from '../../utils/pouncePropsMapper';
import { PounceStyleProps } from '../../utils';
import { Icon } from '../Icon';
import type { IconType } from '../../theme/icons';

// Re-export MUI types
export type { FormHelperTextProps as MuiFormHelperTextProps } from '@mui/material';

// Props interface
export interface FormErrorProps
  extends Omit<FormHelperTextProps, keyof PounceStyleProps>, PounceStyleProps {
  /** The icon type to display. Defaults to 'warning'. */
  icon?: IconType;

  /** The error message content */
  children: React.ReactNode;

  /** Additional styles to apply (combined with pounce props) */
  sx?: FormHelperTextProps['sx'];
}

/**
 * FormError
 *
 * A utility component that quickly allows you to add error messages to form fields.
 * Displays an icon alongside error text with consistent styling.
 */
export const FormError = forwardRef<HTMLDivElement, FormErrorProps>(function FormError(
  { children, icon = 'warning', sx, ...rest },
  ref,
) {
  const { pounceProps, otherProps } = extractPounceProps(rest);
  const pounceStyles = mapPouncePropsToSx(pounceProps);

  const combinedSx = {
    ...pounceStyles,
    ...sx,
  } as SxProps<Theme>;

  return (
    <FormHelperText ref={ref} component="div" role="alert" sx={combinedSx} {...otherProps} error>
      <Icon size="small" type={icon} />
      {children}
    </FormHelperText>
  );
});

FormError.displayName = 'FormError';

export default FormError;
